import type { ColumnDef } from '@tanstack/react-table';
import dayjs from 'dayjs';
import { useLocation, useNavigate } from 'react-router';
import Badge from '@/components/UI/Badge';
import { CurrencyFormatDisplay } from '@/components/UI/NumberFormatDisplay/CurrencyFormatDisplay';
import Table from '@/components/UI/Table';
import { DATE_FORMAT } from '@/constants';
import { useApplicationsListContext } from '@/features/ApplicationForm/Components/ApplicationsList/ApplicationsListProvider';
import { convertEnumToText } from '@/utils';
import { ChicagoRebuildFiltersForm } from './ChicagoRebuildFiltersForm';

export default function ChicagoRebuildApplications() {
  const { data, tablePagination, showFilterModal, toggleFilterModal, applyFiltersHandler } = useApplicationsListContext();

  const navigate = useNavigate();
  const location = useLocation();

  const tableColumns: ColumnDef<any>[] = [
    {
      id: 'businessName',
      header: 'Business Name',
      accessorKey: 'applicationData',
      size: 250,
      cell: (props) => {
        const { getValue } = props;
        const applicationData = getValue() as any;
        const orgInfo = applicationData?.organizationInformation;
        return (
          <div className='text-sm text-neutralColor-medium flex flex-col max-w-md'>
            <span className='truncate'>{orgInfo?.businessName}</span>
            <div className='flex flex-col gap-1 text-xs text-neutralColor-normal'>
              <span>
                {orgInfo?.businessAddress}, {orgInfo?.businessCity}, {orgInfo?.state}, {orgInfo?.zipCode}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      id: 'sponsor',
      header: 'Sponsor',
      accessorKey: 'applicationData',
      size: 250,
      cell: (props) => {
        const { getValue } = props;
        const applicationData = getValue() as any;
        const sponsor = applicationData?.organizationInformation?.sponsors[0];
        return (
          <div className='text-sm text-neutralColor-medium flex flex-col max-w-md'>
            <span className='truncate'>{sponsor.isCorporate ? `${sponsor.leadContact} (corporate)` : sponsor.name}</span>
            {sponsor.isCorporate && <div className='flex flex-col gap-1 text-xs text-neutralColor-normal'>{sponsor?.contactEmail}</div>}
            {!sponsor.isCorporate && (
              <div className='flex flex-col gap-1 text-xs text-neutralColor-normal'>
                <span>
                  {sponsor?.address}, {sponsor?.city}, {sponsor?.state}, {sponsor?.zipCode}
                </span>
              </div>
            )}
          </div>
        );
      },
    },
    {
      id: 'amountRequested',
      header: 'Amount Requested',
      accessorKey: 'applicationData',
      size: 200,
      cell: (props) => {
        const { getValue } = props;
        const applicationData = getValue() as any;
        const financialRequest = applicationData?.financialRequest?.loanRequest;
        return <CurrencyFormatDisplay value={financialRequest?.loanRequestAmount} prefix='$' />;
      },
    },
    {
      id: 'socialDesignation',
      header: 'Social Designation',
      accessorKey: 'applicationData',
      size: 200,
      cell: (props) => {
        const { getValue } = props;
        const applicationData = getValue() as any;
        const orgInfo = applicationData?.organizationInformation;
        return <>{orgInfo?.socialDesignation}</>;
      },
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'status',
      size: 250,
      cell: (props) => {
        const { getValue } = props;
        return <Badge status={getValue() as string} text={convertEnumToText(getValue() as string)} />;
      },
    },
    {
      id: 'createdAt',
      header: 'Submitted',
      accessorKey: 'createdAt',
      size: 250,
      cell: (props) => {
        const { getValue } = props;
        return <div className='flex items-center w-full capitalize'>{dayjs(getValue() as string).format(DATE_FORMAT)}</div>;
      },
    },
  ];

  return (
    <>
      <Table
        cols={tableColumns}
        tableData={data?.data}
        onRowClick={(row: any) => navigate(`${location.pathname}/${row.id}`)}
        pagination={tablePagination}
      />
      {showFilterModal && <ChicagoRebuildFiltersForm handleModal={toggleFilterModal} handleApplyFilter={applyFiltersHandler} />}
    </>
  );
}
